import { useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { zodValidator } from '@tanstack/zod-adapter';
import { z } from 'zod';
import { Box, Flex, Text } from '@chakra-ui/react';
import { Button } from '@/components/ui/Button';
import { JobCategoryTable } from '@/features/job-categories/components/JobCategoryTable';
import { JobCategoryForm } from '@/features/job-categories/components/JobCategoryForm';
import {
  jobCategoryQueries,
  useCreateJobCategory,
} from '@/features/job-categories/api/jobCategoryQueries';

const jobCategoriesSearchSchema = z.object({
  page: z.number().int().positive().catch(1),
  limit: z.number().int().min(1).max(100).catch(20),
  sortBy: z.enum(['name', 'createdAt']).optional().catch(undefined),
  sortOrder: z.enum(['asc', 'desc']).optional().catch(undefined),
  query: z.string().max(100).optional().catch(undefined),
});

export const Route = createFileRoute('/_authenticated/job-categories/')({
  validateSearch: zodValidator(jobCategoriesSearchSchema),
  loaderDeps: ({ search }) => ({ search }),
  loader: ({ context, deps }) =>
    context.queryClient.ensureQueryData(jobCategoryQueries.list(deps.search)),
  component: JobCategoriesPage,
});

function JobCategoriesPage() {
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const [showCreate, setShowCreate] = useState(false);
  const createMutation = useCreateJobCategory();

  return (
    <Box>
      <Flex justify="space-between" align="center" mb={6}>
        <Text fontSize="2xl" fontWeight="bold">
          職種カテゴリマスタ
        </Text>
        <Button colorPalette="brand" onClick={() => setShowCreate((v) => !v)}>
          {showCreate ? 'キャンセル' : '新規作成'}
        </Button>
      </Flex>

      {showCreate && (
        <Box bg="bg.panel" p={4} borderRadius="md" shadow="sm" mb={6}>
          <Text fontWeight="medium" mb={3}>
            新規カテゴリ
          </Text>
          <JobCategoryForm
            onSubmit={async (values) => {
              await createMutation.mutateAsync(values);
              setShowCreate(false);
            }}
            submitLabel="作成"
          />
        </Box>
      )}

      <JobCategoryTable
        search={search}
        onSearchChange={(partial) => navigate({ search: (prev) => ({ ...prev, ...partial }) })}
        onRowClick={(id) =>
          navigate({ to: '/job-categories/$categoryId', params: { categoryId: id } })
        }
      />
    </Box>
  );
}
